import { type TFunction } from 'i18next'
import * as R from 'remeda'

import { type TranslationSchema } from '~/locales/resources'
import { pathFragmentRecord } from '~/routes/paths'
import {
  createBreadcrumbCreatorWithI18n,
  createLabeledPathCreatorWithI18n,
  createMatchedPathKeyHook,
  createPathCompiler,
  createPathParamsHook,
  createPathResolver,
} from '~/routes/utils'

export const rootPath = '/analyzer'

const { brandAccountFragment, brandGroupFragment, surveyFragment } =
  pathFragmentRecord

const pathRecord = {
  top: /*                           */ '/',
  aiSettings: /*                    */ `${brandAccountFragment}/ai-settings`,
  brandAccount: /*                  */ brandAccountFragment,
  brandAccountAllowedDomainList: /* */ `${brandAccountFragment}/allowed-domains`,
  basicOutputGeneration: /*          */ `${surveyFragment}/basic-output-generation`,
  brandAccountUserList: /*          */ `${brandAccountFragment}/users`,
  brandGroupList: /*                */ `${surveyFragment}/brandgroups`,
  brandList: /*                     */ `${surveyFragment}${brandGroupFragment}/brands`,
  createSurvey: /*                  */ `${brandAccountFragment}/new-survey`,
  customerAnalysis: /*              */ `${surveyFragment}/customer-analysis`,
  customerAttributeAnalysis: /*     */ `${surveyFragment}/attribute-analysis`,
  customerAttributeList: /*         */ `${surveyFragment}/attribute-settings/:customerAttributeGroupId/attributes`,
  customerAttributeSettings: /*     */ `${surveyFragment}/attribute-settings`,
  customizedAnalysis: /*            */ `${surveyFragment}/customized-analysis`,
  dataSettings: /*                  */ `${surveyFragment}/data-settings`,
  documentImageArchiveList: /*      */ `${brandAccountFragment}/image-archives`,
  documentImageList: /*             */ `${brandAccountFragment}/images`,
  documentQa: /*                    */ `${brandAccountFragment}/document-qa`,
  documentReportArchiveList: /*     */ `${brandAccountFragment}/document-archives`,
  documentReportEdit: /*            */ `${brandAccountFragment}/documents/:documentReportId`,
  documentReportList: /*            */ `${brandAccountFragment}/documents`,
  overlapAnalysis: /*               */ `${surveyFragment}/overlap-analysis`,
  restoreSurveyFilter: /*           */ `${surveyFragment}/restore-filter`,
  restoreSurveyGroupFilter: /*      */ `${brandAccountFragment}/restore-filter`,
  segmentDefinitions: /*            */ `${surveyFragment}${brandGroupFragment}/segment-definitions`,
  standardAnalysis: /*              */ `${surveyFragment}/standard-analysis`,
  whoAndWhatAnalysis: /*            */ `${surveyFragment}/who-and-what-analysis`,
  strategyMemoArchiveList: /*       */ `${brandAccountFragment}/strategy-archives`,
  strategyMemoEdit: /*              */ `${brandAccountFragment}/strategies/:strategyMemoId`,
  strategyMemoList: /*              */ `${brandAccountFragment}/strategies`,
  survey: /*                        */ surveyFragment,
  surveySettings: /*                */ `${surveyFragment}/settings`,
  timeSeriesAnalysis: /*            */ `${brandAccountFragment}/time-series`,
  timeSeriesSettings: /*            */ `${brandAccountFragment}/time-series-settings`,
} as const

type PathRecord = typeof pathRecord
export type PathKey = keyof PathRecord
export type PathValue = ValueOf<PathRecord>
export type PathParams<Key extends PathKey> = AnalyzePathParams<PathRecord[Key]>

export const pathKeys = R.keys(pathRecord) as PathKey[]

export const pathLabelI18nKeyMap = {
  top: 'top',
  aiSettings: 'ai_settings',
  basicOutputGeneration: 'basic_output_generation',
  brandAccount: 'brand_account_1',
  brandAccountAllowedDomainList: 'allowed_domains',
  brandAccountUserList: 'users',
  brandGroupList: 'brand_group_list',
  brandList: 'brand_list_1',
  createSurvey: 'create_new_survey',
  customerAnalysis: '9segs_customer_distribution',
  customerAttributeAnalysis: 'customized_segments_analysis',
  customerAttributeList: 'customized_segments_list',
  customerAttributeSettings: 'customized_segments_settings_2',
  customizedAnalysis: '9segs_flexible_mode',
  dataSettings: 'data_upload_1',
  documentImageArchiveList: 'bin_2',
  documentImageList: 'tables_and_graphs',
  documentQa: 'document_qa',
  documentReportArchiveList: 'bin_2',
  documentReportEdit: 'document',
  documentReportList: 'documents',
  overlapAnalysis: 'overlap_analysis',
  restoreSurveyFilter: 'restore_filter',
  restoreSurveyGroupFilter: 'restore_filter_time_series_data',
  segmentDefinitions: '9segs_definition_1',
  standardAnalysis: '9segs_customer_data',
  whoAndWhatAnalysis: 'who_and_what_analysis',
  strategyMemoArchiveList: 'bin_2',
  strategyMemoEdit: 'customer_strategy',
  strategyMemoList: 'strategies',
  survey: 'survey_3',
  surveySettings: 'survey_information',
  timeSeriesAnalysis: 'time_series_analysis',
  timeSeriesSettings: 'settings_1',
} as const satisfies Record<PathKey, keyof TranslationSchema>

export const pathGroupLabelI18nKeyMap = {
  advancedAnalysis: 'deep_dive_analysis',
  basicAnalysis: 'core_analysis',
  brandAccountSettings: 'brand_account_settings',
  documentManagement: 'documents_1',
  settings: 'survey_settings',
  strategyMemoManagement: 'customer_strategy_1',
  timeSeriesReport: 'time_series',
} as const satisfies Record<string, keyof TranslationSchema>

export type PathGroupKey = keyof typeof pathGroupLabelI18nKeyMap

export const pathGroupScreenLabelMap = {
  advancedAnalysis: [
    'customizedAnalysis',
    'overlapAnalysis',
    'customerAttributeAnalysis',
    'whoAndWhatAnalysis',
  ],
  basicAnalysis: [
    'basicOutputGeneration',
    'customerAnalysis',
    'standardAnalysis',
  ],
  brandAccountSettings: [
    'brandAccountUserList',
    'brandAccountAllowedDomainList',
    'aiSettings',
  ],
  documentManagement: ['documentReportList', 'documentImageList', 'documentQa'],
  settings: ['surveySettings', 'dataSettings', 'brandGroupList'],
  strategyMemoManagement: ['strategyMemoEdit', 'strategyMemoList'],
  timeSeriesReport: ['timeSeriesAnalysis', 'timeSeriesSettings'],
} as const satisfies Record<PathGroupKey, PathKey[]>

export const useMatchedPathKey = createMatchedPathKeyHook(pathRecord, rootPath)
export const usePathParams = createPathParamsHook(pathRecord)
export const compile = createPathCompiler(pathRecord, rootPath)
export const resolve = createPathResolver(pathRecord)

// eslint-disable-next-line jsdoc/require-param, jsdoc/require-returns
/**
 * TODO: [i18n改善] t関数を引数で受け取る設計を見直す
 * 翻訳キーを返し、呼び出し側でt()を適用する形に変更することで、保守性の改善を行う
 */
export const createLabeledPathWithI18n = (t: TFunction) =>
  createLabeledPathCreatorWithI18n(pathRecord, pathLabelI18nKeyMap, t, rootPath)

// eslint-disable-next-line jsdoc/require-param, jsdoc/require-returns
/**
 * TODO: [i18n改善] t関数を引数で受け取る設計を見直す
 * 翻訳キーを返し、呼び出し側でt()を適用する形に変更することで、保守性の改善を行う
 */
export const createBreadcrumbWithI18n = (t: TFunction) =>
  createBreadcrumbCreatorWithI18n(
    pathRecord,
    pathLabelI18nKeyMap,
    pathGroupLabelI18nKeyMap,
    t,
    rootPath,
  )
